import React from 'react';
import styled, { keyframes } from 'styled-components';
import PickerHeader from './PickerHeader';

const Content = styled.div`
  padding: 0 1em 1em;
  display: flex;
  flex-direction: column;
`;

const PrioritySlots = styled.div`
  display: grid;
  grid-gap: 0.5rem;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  flex: 1;
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const SlotSpinner = styled.div`
  border: 4px solid rgba(255, 255, 255, 0.2);
  border-top-color: #ffffff;
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
  animation: ${spin} 0.8s linear infinite;
`;

const PrioritySlotWrapper = styled.button<{ selected?: boolean }>`
  border: none;
  color: inherit;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${props => (props.selected ? '#00b641' : 'rgba(255, 255, 255, 0.1)')};
  font-size: 4rem;
  border-radius: 0.25rem;

  &:active {
    background: #00b641;
  }

  &:disabled {
    opacity: ${props => (props.selected ? 1 : 0.25)};
    background: ${props => (props.selected ? '#00b641' : 'rgba(255, 255, 255, 0.05)')};
    color: ${props => (props.selected ? 'inherit' : 'rgba(255, 255, 255, 0.3)')};
    cursor: not-allowed;
  }
`;

const PrioritySlot = ({
  priority,
  disabled,
  onClick,
  selected,
  showSpinner,
}: {
  priority: number;
  disabled: boolean;
  onClick: (priority: number) => void;
  selected?: boolean;
  showSpinner?: boolean;
}) => (
  <PrioritySlotWrapper selected={selected} disabled={disabled} onClick={() => !disabled && onClick(priority)}>
    {showSpinner ? <SlotSpinner /> : <span>{priority}</span>}
  </PrioritySlotWrapper>
);

const PatientPriorityPicker = ({
  priorityCounter,
  assignedPriority,
  onAssignPriority,
  goToStartPage,
  onCancel,
}: {
  priorityCounter: number;
  assignedPriority?: string;
  onAssignPriority: (priority: number) => void;
  goToStartPage: () => void;
  onCancel: () => void;
}) => {
  const [loading, setLoading] = React.useState(false);
  const [selectedPriority, setSelectedPriority] = React.useState<number | null>(null);

  const handleAssignPriority = async (priority: number) => {
    if (loading) {
      return;
    }

    try {
      setSelectedPriority(priority);
      setLoading(true);
      await onAssignPriority(priority);
      goToStartPage();
    } catch (e) {
      console.error('Error assigning bed priority:', e);
      setLoading(false);
    }
  };

  return (
    <Content>
      <PickerHeader onCancel={onCancel} title={'Bed Priority'} bedInput={undefined} />
      <PrioritySlots>
        {Array.from(Array(priorityCounter).keys()).map((p: number) => (
          <PrioritySlot
            key={p}
            disabled={loading || `${p + 1}` === assignedPriority}
            priority={p + 1}
            onClick={handleAssignPriority}
            selected={`${p + 1}` === assignedPriority}
            showSpinner={loading && selectedPriority === p + 1}
          />
        ))}
      </PrioritySlots>
    </Content>
  );
};

export default PatientPriorityPicker;
